import { useEffect, useRef, useState } from "react";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { FeedRelatedArticles } from "@routes/Article/utils/types";
import { gaEventTracker } from "@util/helpers";

export type RelatedArticlesProps = {
  readMoreArticle?: FeedRelatedArticles[];
  /** Additional parameters to add on the links, used in GA */
  linkParams?: URLSearchParams;
};

const variant: CardVariations = {
  image: {
    position: "left",
    width: "w-[70px] lg:w-[90px]",
    imageWrapperClass: "aspect-3x2",
    imageClassName: "object-cover",
  },
  content: {
    width: "w-full-120px lg:w-2/3",
  },
  title: {
    color: "!text-gray-850",
    size: "!text-lg",
    extraClass: "my-0 leading-[1.3rem] tracking-normal font-semibold !font-lct",
  },
};

type ArticleReadMoreProps = {
  relatedArticle: FeedRelatedArticles;
  /** Additional parameters to add on the links, used in GA */
  params: string;
};

const ArticleIndividualCard = ({
  relatedArticle,
  params,
}: ArticleReadMoreProps) => {
  const uniqueName = relatedArticle?.sections?.[0]?.uniqueName || "";

  return (
    <div
      key={relatedArticle.id}
      className="mb-6"
      data-testid="article-read-more-individual-card-component"
    >
      <Card
        id={relatedArticle.id}
        title={relatedArticle.title}
        media={relatedArticle.media?.[0]}
        variations={variant}
        defaultImage={{
          directoryName: uniqueName,
        }}
        className={"gap-4"}
        link={`${relatedArticle.urlPath}${params}`}
      />
    </div>
  );
};

const ArticleReadMore = ({
  readMoreArticle,
  linkParams,
}: RelatedArticlesProps): React.ReactElement => {
  const [childrenHeight, setChildrenHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const childrenRef = useRef(null);
  const windowSize = useWindowSize();

  const params = linkParams ? `?${linkParams?.toString()}` : "";

  useEffect(() => {
    if (childrenRef.current) {
      setChildrenHeight((childrenRef.current as HTMLDivElement).scrollHeight);
    }
  }, [windowSize]);

  const toggleMoreLess = () => {
    gaEventTracker("see also", "click", !isExpanded ? "more" : "less");
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      {readMoreArticle && readMoreArticle.length > 0 ? (
        <div
          className="no-print mb-6 mt-8 border border-gray-175 p-6 md:-mx-8 md:mt-0 md:p-8"
          data-testid="article-read-more-component"
        >
          <div>
            <p className="mb-6 border-b border-gray-250 pb-4 font-poppins text-4xs font-medium tracking-widest text-gray-515">
              SEE ALSO
            </p>

            <div data-testid="section-article-read-more">
              {readMoreArticle
                .slice(0, 3)
                .map((relatedArticle: FeedRelatedArticles) => {
                  return relatedArticle.id && relatedArticle.title ? (
                    <ArticleIndividualCard
                      key={relatedArticle.id}
                      relatedArticle={relatedArticle}
                      params={params}
                    />
                  ) : null;
                })}
            </div>

            {readMoreArticle.length > 3 ? (
              <>
                <div
                  ref={childrenRef}
                  data-testid="section-related-stories"
                  className="overflow-hidden transition-all duration-300"
                  style={{
                    height:
                      isExpanded && readMoreArticle.length > 3
                        ? childrenHeight
                        : 0,
                  }}
                >
                  {readMoreArticle
                    .slice(3)
                    .map((relatedArticle: FeedRelatedArticles) => {
                      return relatedArticle.id && relatedArticle.title ? (
                        <ArticleIndividualCard
                          key={relatedArticle.id}
                          relatedArticle={relatedArticle}
                          params={params}
                        />
                      ) : null;
                    })}
                </div>

                <div className="w-full">
                  {isExpanded ? (
                    <div
                      className="cursor-pointer border-t border-gray-175 pt-3 text-right !font-poppins text-base font-semibold capitalize tracking-normal !text-gray-850 [@media(hover:hover)]:hover:underline"
                      onClick={toggleMoreLess}
                    >
                      Less
                    </div>
                  ) : null}

                  {!isExpanded ? (
                    <div
                      className="cursor-pointer border-t border-gray-175 pt-3 text-right !font-poppins text-base font-semibold capitalize tracking-normal !text-gray-850 [@media(hover:hover)]:hover:underline"
                      onClick={toggleMoreLess}
                    >
                      More
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ArticleReadMore;
